<script setup lang="ts">
  import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { CircleX } from 'lucide-vue-next';
  import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
  import { useModal } from 'vue-final-modal';

  // Components
  import { DocumentViewInfo as DocumentInfo } from '@/features/Document';
  import {
    ApprovalOptions,
    DocumentApprovalActions,
    JUSTIFICATION_MAX_LENGTH,
    TrainingSection,
  } from '@/pages/approval';
  import {
    ChangelogSection,
    PositionUserSelector,
    NotesSection,
    ConfirmDeleteModal,
  } from '@/shared/ui';

  // API
  import { approvalApi } from '@/pages/approval';

  // Composables
  import { getApprovalDocumentErrorMessage, useDocumentApproval } from '@/pages/approval';
  import { approvalItemGuard } from '../../model/approvalItem.guard';
  import {
    isRecordDetailRoute,
    useCustomToast,
    formatDateForApi,
    setBreadcrumbLabel,
    clearBreadcrumbLabel,
  } from '@/shared/lib';

  // Misc
  import { useNotificationStore } from '@/entities/Notification';
  import { useUserStore } from '@/entities/User';
  import {
    fetchAllMembersAsCollaborators,
    useBranchTheme,
  } from '@/entities/Branch';
  import { resolveTrainingBranchIdForHomologation } from '@/features/Document';
  import type { UserItem } from '@/shared/lib';

  const route = useRoute();
  const router = useRouter();
  const userStore = useUserStore();
  const approvalOptionsRef = ref<InstanceType<typeof ApprovalOptions>>();
  const { fetchApprovalCount, fetchVerificationCount, fetchAwaitingCpCount } =
    useNotificationStore();

  const toast = useCustomToast();
  const { getCurrentSelectedBranch } = useBranchTheme();

  const fullInstructors = ref<UserItem[]>([]);
  const allBranchMembers = ref<UserItem[]>([]);

  const loadingDocumentApproval = ref(false);

  const resetTrainingSessions = ref(true);

  const detailLoad = ref<Promise<void> | null>(null);
  let isPageActive = true;

  const id = route.params.id as string;
  const {
    pendingDocument,
    selectedOption,
    trainingOption,
    showAdditionalField,
    positions,
    users,
    selectedPositions,
    selectedUsers,
    selectedApprovalPosition,
    changelogLines,
    detailConfig,
    isSaveButtonActive,
    positionsSelectedCount,
    usersSelectedCount,
    documentInfo,
    reprovalJustification,
    trainingJustification,
    fetchPendingDocumentIdData,
    notes,
    trainingSessions,
    approvalPositionOptions,
    fetchUserPositions,
  } = useDocumentApproval();

  onMounted(async () => {
    detailLoad.value = (async () => {
      await userStore.me();
      await Promise.all([fetchPendingDocumentIdData(id, isRecord.value), fetchUserPositions()]);
    })();

    try {
      await detailLoad.value;

      const trainingBranchId = resolveTrainingBranchIdForHomologation(
        pendingDocument.value?.data?.document?.branchId,
        getCurrentSelectedBranch()
      );

      allBranchMembers.value =
        trainingBranchId !== undefined
          ? await fetchAllMembersAsCollaborators(trainingBranchId)
          : [];

      const elaboratorId = pendingDocument.value?.data?.document?.user?.id;
      const approverId = userStore.user?.id;
      const instructorUserIds = new Set(
        [elaboratorId, approverId].filter((memberId): memberId is number => memberId != null)
      );

      fullInstructors.value = allBranchMembers.value.filter((member) =>
        member.userId != null && instructorUserIds.has(member.userId)
      );
    } catch (error) {
      toast.showErrorToast({
        title: 'Erro',
        message: getApprovalDocumentErrorMessage(error, 'Erro ao carregar dados do documento'),
        type: 'error',
      });
    }
  });

  const approvalListRoute = computed(() => ({
    path: '/aprovacao',
    query: { type: detailConfig.value.listTabType },
  }));

  // A natureza vem do segmento da rota, e vale antes de qualquer chamada: `/aprovacao/61` e
  // `/aprovacao/registro/61` sao itens diferentes, de tabelas diferentes.
  const isRecord = computed(() => isRecordDetailRoute(route.params.nature));

  // A Registration has no file to deliver: the card button opens the dedicated screen that draws
  // the matrix form. The id that arrives here is the matrix id, and the route segment tells them
  // apart.
  const recordViewRoute = computed(() =>
    isRecord.value ? { name: 'AprovacaoVerRegistro', params: { id } } : undefined
  );

  const goToApprovalList = async () => {
    await detailLoad.value?.catch(() => undefined);

    if (!isPageActive) {
      return;
    }

    router.push(approvalListRoute.value);
  };

  watch(
    () => pendingDocument.value?.data?.document?.prefix,
    (itemCode) => {
      if (itemCode) {
        setBreadcrumbLabel(itemCode);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    isPageActive = false;
    clearBreadcrumbLabel();
  });

  // A notice for another item only changes the params, which skips the route's `beforeEnter`.
  onBeforeRouteUpdate(approvalItemGuard);

  const { open: openConfirmation, close: closeConfirmation } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      get title() {
        return detailConfig.value.rejectConfirmTitle;
      },
      description: 'A ação não pode ser desfeita.',
      icon: markRaw(CircleX),
      iconAlt: 'Reprovar',
      onClose() {
        closeConfirmation();
        loadingDocumentApproval.value = false;
      },
      async onConfirm() {
        const justification = approvalOptionsRef.value?.getReprovalJustification();

        if (!justification) {
          toast.showErrorToast({
            title: 'Erro',
            message: 'Justificativa é obrigatória',
            type: 'error',
          });
          loadingDocumentApproval.value = false;
          return;
        }

        try {
          const positionId = selectedApprovalPosition.value
            ? Number(selectedApprovalPosition.value)
            : undefined;

          // O Registro vive em outra tabela, entao a decisao dele tem rota propria.
          if (isRecord.value) {
            await approvalApi.decideMatrixApproval({
              matrixId: parseInt(id),
              status: 'REJECTED',
              positionId,
              justification,
            });
          } else {
            await approvalApi.rejectDocumentWithPosition({
              documentRevisionId: parseInt(id),
              justification,
              positionId,
            });
          }

          await fetchApprovalCount();
          await fetchVerificationCount();
          await fetchAwaitingCpCount();
          toast.showSuccessToast({
            title: detailConfig.value.rejectSuccessTitle,
            message: 'Os usuários envolvidos serão notificados.',
            type: 'success',
          });

          closeConfirmation();
          await goToApprovalList();
        } catch (error) {
          toast.showErrorToast({
            title: 'Erro',
            message: getApprovalDocumentErrorMessage(error, detailConfig.value.rejectErrorMessage),
            type: 'error',
          });

          closeConfirmation();
          loadingDocumentApproval.value = false;
        } finally {
          loadingDocumentApproval.value = false;
        }
      },
    },
  });

  const handleSelectOption = (option: string) => {
    selectedOption.value = option;
    if (option === 'reprovar') {
      trainingOption.value = undefined;
      showAdditionalField.value = false;
      selectedPositions.value = [];
      selectedUsers.value = [];
      trainingSessions.value = [];
      resetTrainingSessions.value = true;
      selectedApprovalPosition.value = '';
    } else if (option === 'aprovar') {
      reprovalJustification.value = undefined;
    }
  };

  const handleCancel = async () => {
    await goToApprovalList();
  };

  const resolveTrainingResponsibleMemberId = (memberId: number): number | undefined => {
    const pool = [...(users.value.data ?? []), ...fullInstructors.value];
    const member = pool.find(
      (item) =>
        Number(item.memberId ?? item.membership?.id ?? item.id) === Number(memberId)
    );

    if (!member) {
      return undefined;
    }

    return Number(member.memberId ?? member.membership?.id ?? member.id);
  };

  const buildTrainingSessionsPayload = (): {
    trainingDate: string;
    responsibleMemberId: number;
    responsibleUserPositionId: number;
  }[] | null => {
    const sessionsPayload: {
      trainingDate: string;
      responsibleMemberId: number;
      responsibleUserPositionId: number;
    }[] = [];

    for (const trainingSession of trainingSessions.value) {
      const selectedMemberId = trainingSession.responsibleMemberId;
      const responsibleUserPositionId = trainingSession.responsibleUserPositionId;
      if (selectedMemberId == null || responsibleUserPositionId == null) continue;

      const responsibleMemberId = resolveTrainingResponsibleMemberId(selectedMemberId);
      if (responsibleMemberId === undefined) {
        toast.showErrorToast({
          title: 'Erro',
          message: 'Não foi possível identificar o membro na filial para o ministrante selecionado.',
          type: 'error',
        });
        return null;
      }

      sessionsPayload.push({
        trainingDate: trainingSession.trainingDate as string,
        responsibleMemberId,
        responsibleUserPositionId,
      });
    }

    return sessionsPayload;
  };

  // O Registro vive em outra tabela, entao a decisao dele tem rota propria.
  const submitApproval = async (approvePayload: { documentRevisionId: number; positionId?: number }) => {
    if (isRecord.value) {
      await approvalApi.decideMatrixApproval({
        matrixId: parseInt(id),
        status: 'APPROVED',
        positionId: approvePayload.positionId,
      });
      return;
    }

    await approvalApi.approveDocumentWithSessions(approvePayload);
  };

  const handleSave = async () => {
    loadingDocumentApproval.value = true;
    if (selectedOption.value === 'reprovar') {
      openConfirmation();
      return;
    }

    const approvePayload: {
      documentRevisionId: number;
      trainingJustification?: string;
      notes?: string;
      users?: number[];
      positions?: number[];
      positionId?: number;
      sessions?: {
        trainingDate: string;
        responsibleMemberId: number;
        responsibleUserPositionId: number;
      }[];
    } = {
      documentRevisionId: parseInt(id),
    };

    if (selectedOption.value === 'aprovar') {
      approvePayload.positionId = Number(selectedApprovalPosition.value);
    }

    if (detailConfig.value.hasTrainingStep) {
      if (trainingOption.value === 'nao' && showAdditionalField.value) {
        approvePayload.trainingJustification = trainingJustification.value as string;
      } else {
        approvePayload.positions = selectedPositions.value.map((selectedPosition) =>
          Number(selectedPosition)
        );

        approvePayload.users = selectedUsers.value.map((selectedUser) => Number(selectedUser));

        if (notes.value) {
          approvePayload.notes = notes.value;
        }

        const sessionsPayload = buildTrainingSessionsPayload();
        if (!sessionsPayload) {
          loadingDocumentApproval.value = false;
          return;
        }

        approvePayload.sessions = sessionsPayload;
      }
    }

    try {
      await submitApproval(approvePayload);

      await fetchApprovalCount();
      await fetchVerificationCount();
      await fetchAwaitingCpCount();
      toast.showSuccessToast({
        title: 'Aprovação salva com sucesso.',
        type: 'success',
      });

      await goToApprovalList();
    } catch (error: unknown) {
      toast.showErrorToast({
        title: 'Erro',
        message: getApprovalDocumentErrorMessage(error),
        type: 'error',
      });
    } finally {
      loadingDocumentApproval.value = false;
    }
  };

  watch(trainingOption, () => {
    selectedPositions.value = [];
    selectedUsers.value = [];
    trainingSessions.value = [];
    resetTrainingSessions.value = true;
  });

  const showTrainingSection = computed(
    () => trainingOption.value === 'sim' && showAdditionalField.value
  );

  const instructorsList = computed(() => fullInstructors.value);

  const handleNotesChange = (event: Event | string | undefined) => {
    notes.value =
      typeof event === 'string' ? event : (event?.target as HTMLTextAreaElement | undefined)?.value;
  };

  const handleTrainingSectionsChange = (
    event: {
      id: number;
      title: string;
      selectedInstructor: string | number | null;
      selectedInstructorPosition: string | number | null;
      selectedDate: Date | null;
      isDatePickerOpen: boolean;
    }[]
  ) => {
    trainingSessions.value = event.map((section) => {
      const normalizedInstructor = String(section.selectedInstructor ?? '').trim();
      const normalizedInstructorPosition = String(section.selectedInstructorPosition ?? '').trim();
      const parsedInstructorId = Number(normalizedInstructor);
      const parsedInstructorPositionId = Number(normalizedInstructorPosition);
      const hasInstructor = normalizedInstructor !== '' && !Number.isNaN(parsedInstructorId);
      const hasInstructorPosition =
        normalizedInstructorPosition !== '' &&
        !Number.isNaN(parsedInstructorPositionId) &&
        parsedInstructorPositionId > 0;

      return {
        trainingDate: hasInstructor && hasInstructorPosition && section.selectedDate
          ? formatDateForApi(section.selectedDate)
          : null,
        responsibleMemberId: hasInstructor ? parsedInstructorId : null,
        responsibleUserPositionId: hasInstructorPosition ? parsedInstructorPositionId : null,
      };
    });
  };

  const handleResetTrainingSessionsChange = () => {
    resetTrainingSessions.value = false;
  };

  defineExpose({
    selectedOption,
    trainingOption,
    handleSave,
    openConfirmation,
    closeConfirmation,
  });
</script>

<template>
  <section class="pt-4 p-6 text-gray-07">
    <div class="mt-4 mb-6 text-xl font-semibold flex justify-start">
      <span>Etapa de Análise Crítica e Aprovação</span>
    </div>

    <div
      class="w-full border border-border-color divide-y divide-gray-02 rounded-2xl text-sm bg-white-01"
    >
      <div class="flex font-semibold items-center py-4 pl-6 pr-10">
        <span>{{ detailConfig.cardTitle }}</span>
      </div>

      <div class="p-6">
        <DocumentInfo
          :info="documentInfo"
          :document-id="pendingDocument?.data?.document?.id?.toString() as string"
          :view-route="recordViewRoute"
          :view-button-label="detailConfig.viewButtonLabel"
        />
      </div>

      <ChangelogSection :changelog="changelogLines" />

      <ApprovalOptions
        ref="approvalOptionsRef"
        v-model:reproval-justification="reprovalJustification"
        v-model:training-justification="trainingJustification"
        v-model:selected-approval-position="selectedApprovalPosition"
        :selected-option="selectedOption"
        :training-option="trainingOption"
        :show-additional-field="showAdditionalField"
        :approval-position-options="approvalPositionOptions"
        :approve-option-label="detailConfig.approveOptionLabel"
        :reject-option-label="detailConfig.rejectOptionLabel"
        :reject-placeholder="detailConfig.rejectPlaceholder"
        @update:selected-option="handleSelectOption"
        @update:training-option="trainingOption = $event"
        @update:training-justification="trainingJustification = $event"
      />

      <div
        v-if="showTrainingSection"
        class="flex flex-col items-start gap-2 px-6 py-8"
      >
        <div class="font-semibold">
          Selecione os colaboradores que deverão passar pelo treinamento
        </div>

        <PositionUserSelector
          :positions="positions"
          :users="users"
          :selected-positions="selectedPositions"
          :selected-users="selectedUsers"
          :positions-selected-count="positionsSelectedCount"
          :users-selected-count="usersSelectedCount"
          @update:selected-positions="selectedPositions = $event"
          @update:selected-users="selectedUsers = $event"
        />
      </div>

      <TrainingSection
        v-if="showTrainingSection"
        :users="users"
        :instructors="instructorsList"
        :reset-training-sessions="resetTrainingSessions"
        @update:reset-training-sessions="handleResetTrainingSessionsChange"
        @selected-training-sections="handleTrainingSectionsChange"
      />

      <NotesSection
        v-if="showTrainingSection"
        title="Observações"
        placeholder="Descreva qualquer observação sobre o treinamento"
        :max-length="JUSTIFICATION_MAX_LENGTH"
        @update:notes="handleNotesChange"
      />
    </div>
    <DocumentApprovalActions
      :is-save-button-active="isSaveButtonActive"
      :is-loading="loadingDocumentApproval"
      @cancel="handleCancel"
      @save="handleSave"
    />
  </section>
</template>
